import type { CartArticleType } from 'services/types';
import { formatPrice, CART_ARTICLE_GROUP, CART_ARTICLE_TYPE, type LocaleIso } from 'utils';

export interface ArticleScalePrice {
  /** Discount in percent compared to the price without graduated scale. */
  percentage: number;
  /** Total with graduated scale applied — the price the customer pays. */
  scaledTotal: number;
  /** Total without graduated scale — shown struck through. */
  grossTotal: number;
}

/** Only photo prints (article group "photos" of type "group") have graduated prices. */
export function isScalableArticle({ articleGroup, type }: Pick<CartArticleType, 'articleGroup' | 'type'>) {
  return articleGroup === CART_ARTICLE_GROUP.PHOTOS && type === CART_ARTICLE_TYPE.GROUP;
}

const roundPrice = (price: number) => Math.round(price * 100) / 100;

/**
 * Returns the graduated price info, or null when no discount applies.
 * A scaled price of 0 means the PSI field was missing, not a free article — never show "100%" for it.
 * For photo prints PSI's summaryPriceGross/summaryPriceGrossScaled already are the full totals, so no details are added.
 */
export function getArticleScalePrice(gross: number, scaled: number): ArticleScalePrice | null {
  if (gross <= 0 || scaled <= 0 || scaled >= gross) return null;

  return {
    percentage: Math.round((100 * (gross - scaled)) / gross),
    scaledTotal: roundPrice(scaled),
    grossTotal: roundPrice(gross)
  };
}

/** Display values for the struck-through price; shared by the SSR and live render so both format identically. */
export function formatArticleScale(scalePrice: ArticleScalePrice | null, locale: LocaleIso) {
  if (!scalePrice) return undefined;

  return { percentage: scalePrice.percentage, originalPrice: formatPrice(scalePrice.grossTotal, locale) ?? '' };
}
